import type {
  Artifact,
  ArtifactKind,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { type ComponentType, createElement, type ReactElement } from 'react'

import { documentKind } from './DocumentKind'

/** How one artifact kind shows in the panel and what its Download button produces. */
export interface ArtifactKindRenderer<A extends Artifact = Artifact> {
  Body: ComponentType<{ artifact: A; changedPaths: string[] }>
  download: {
    extension: 'pdf'
    mimeType: string
    render: (artifact: A) => Promise<Blob>
  }
}

type ArtifactOfKind<K extends ArtifactKind> = Extract<Artifact, { kind: K }>

export const ARTIFACT_KINDS: {
  [K in ArtifactKind]: ArtifactKindRenderer<ArtifactOfKind<K>>
} = {
  document: documentKind,
}

/** One artifact bound to its kind's renderer: the panel body and the Download file. */
export interface ArtifactRendering {
  body: (changedPaths: string[]) => ReactElement
  download: {
    extension: 'pdf'
    mimeType: string
    render: () => Promise<Blob>
  }
}

function bindKind<A extends Artifact>(
  kind: ArtifactKindRenderer<A>,
  artifact: A
): ArtifactRendering {
  const { Body, download } = kind
  return {
    body: (changedPaths) => createElement(Body, { artifact, changedPaths }),
    download: {
      extension: download.extension,
      mimeType: download.mimeType,
      render: () => download.render(artifact),
    },
  }
}

export function artifactRendering(artifact: Artifact): ArtifactRendering {
  switch (artifact.kind) {
    case 'document':
      return bindKind(ARTIFACT_KINDS.document, artifact)
  }
}
